<script setup>
import { IconLayoutSidebarRightExpand } from "@tabler/icons-vue";

import Icon from "./Icon.vue";
import Navbar from "@/components/v4/layout/navbar/Navbar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/v4/tooltip";
import WorkspaceModal from "@/views/dashboard/components/onboarding/WorkspaceModal.vue";
import getDecodedUserId from "@/helpers/get-decoded-user-id";
import { getAppId } from "@/helpers/app-id";
import { useQuery } from "@tanstack/vue-query";

import { cn } from "@/lib/utils";

import {
  ref,
  computed,
  onBeforeMount,
  getCurrentInstance,
  watchEffect,
  provide,
  inject,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import Hashids from "hashids";
import { useStore } from "vuex";
import { useCookies } from "vue3-cookies";
import {
  sortBy,
  some,
  size,
  find,
  findIndex,
  debounce,
  filter,
  clone,
} from "lodash";

import Toast from "@/helpers/toast.helper";

import { httpClientOmni, httpPlain } from "@/libraries";

const { proxy } = getCurrentInstance();
const axios = proxy.$axios;
const router = proxy.$router;

const props = defineProps({
  class: String,
});

// Dependencies
const { cookies } = useCookies();
const route = useRoute();
const store = useStore();

// Reactive state
const url = computed(() => route.path.split("/"));
const isExpanded = ref(false);
const activeModule = ref({});
const hasSubModule = ref(false);

const emitter = inject("emitter");

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};
const access = computed(() => store.getters["accessibility/getAccess"]);

const accessibilityUrlV3 = computed(
  () =>
    `${import.meta.env.VITE_OMNICHANNEL_URL}/api/${getAppId()}/v4/accessibility`
);

const queryGetMenus = useQuery({
  queryKey: ["QUERY_GET_MENUS"],
  queryFn: async () => {
    const response = await httpClientOmni.get("v4/accessibility");
    return response.data.data;
  },
  staleTime: Infinity,
});

const queryUserData = useQuery({
  queryKey: ["QUERY_GET_USER_DATA"],
  queryFn: async () => {
    const response = await httpPlain.get(`user-platform/user-data`);
    return response.data?.data;
  },
  staleTime: Infinity,
});

const modulesSorted = computed(() => {
  const fetchedModules = queryGetMenus.data?.value?.module || [];
  const sorted = sortBy(fetchedModules, ["order"]);
  return sorted;
});

// Debounced accessibility checker
const accessibilityChecker = async () => {
  try {
    await store.dispatch("accessibility/getRoleV3");

    const decodedUserId = getDecodedUserId();
    if (decodedUserId) {
      await store.dispatch("integration/getIntegrationData");
      fetchBotIntegration();

      const response = await axios.get(accessibilityUrlV3.value);
      if (!response.data.success) {
        router.replace({ name: "notfound" });
        store.dispatch("sidebar/updateModules", null);
        return Toast.error(response.data.message);
      }

      // Set accessibility data
      store.dispatch("accessibility/setAccessibility", response.data.data);
      store.dispatch("sidebar/updateModules", access.value.module);

      const listApps = some(access.value.apps, {
        id: route.params.appId,
      });

      if (!listApps) {
        router.push({ name: "forbidden" });
        store.dispatch("sidebar/updateModules", null);
        return Toast.error(
          "You need invited to this app before access, contact your admin!"
        );
      }

      // App access check
      const canAccessApp = some(access.value.apps, {
        id: route.params.appId,
      });

      if (!canAccessApp) {
        router.push({ name: "forbidden" });
        store.dispatch("sidebar/updateModules", null);
        return Toast.error(
          "You need invited to this app before access, contact your admin!"
        );
      }

      // Module access check
      const currentModuleUrl = url.value[2];
      const canAccessModule = some(
        access.value.module,
        (o) => o.url === currentModuleUrl
      );

      if (!canAccessModule) {
        if (router.currentRoute.value.name !== "billing") {
          goToNearestModuleMenu(access.value.module);
        }
      } else {
        const currentModuleData = find(
          access.value.module,
          (m) => m.url === currentModuleUrl
        );
        if (currentModuleData?.v3_menus?.length > 0) {
          const currentMenuUrl = url.value[3];
          const canAccessMenu = some(
            currentModuleData.v3_menus,
            (o) => o.url === currentMenuUrl
          );

          if (!canAccessMenu) {
            // goToNearestModuleMenu(access.value.module);
          }
        }
      }

      await store.dispatch("integration/getIntegrationData", "bot");
    } else {
      router.push({ name: "login" });
    }
  } catch (error) {
    Toast.error("Gagal mendapatkan akses data. Silakan login ulang.");
    router.push({ name: "login" });
  }
};

const goToNearestModuleMenu = (modules) => {
  const moduleUrl = url.value[2];
  const moduleIdx = findIndex(modules, function (module) {
    return module.url == moduleUrl;
  });
  if (moduleIdx == -1) {
    if (size(modules[0]?.v3_menus) > 0) {
      return moduleHaveMenus(modules, 0);
    }
  }
  if (size(modules[moduleIdx]?.v3_menus) > 0) {
    return moduleHaveMenus(modules, moduleIdx);
  }
};

const moduleHaveMenus = (modules, moduleIdx) => {
  const menuUrl = url.value[3];
  const menuIdx = findIndex(modules[moduleIdx].v3_menus, function (menu) {
    return menu.url == menuUrl;
  });
  if (menuIdx !== -1) {
    goToCurrentMenuOfCurrentModule(modules, moduleIdx, menuIdx);
  }
};

const goToCurrentMenuOfCurrentModule = async (modules, moduleIdx, menuIdx) => {
  const submenuUrl = url.value[4];
  if (size(modules[moduleIdx].v3_menus[menuIdx].submenus) > 0) {
    const submenuIdx = findIndex(
      modules[moduleIdx].v3_menus[menuIdx].submenus,
      function (s) {
        return s.url == submenuUrl;
      }
    );
    if (submenuIdx !== -1) {
      const targetName = `${modules[moduleIdx].slug}.${modules[moduleIdx].v3_menus[menuIdx].slug}.${modules[moduleIdx].v3_menus[menuIdx].submenus[submenuIdx].slug}`;
      if (!router.hasRoute(targetName)) {
        return;
      }
      return router.push({ name: targetName }).catch((err) => {
        if (err.name !== "NavigationDuplicated") {
          console.warn("[V4Layout] navigation failed:", targetName, err);
        }
      });
    }
  }
};

const validateActiveBot = (channels, activeBotId) => {
  let result = false;
  for (const channel of channels) {
    for (const integration of channel.integration) {
      if (integration.bot_id == activeBotId) {
        result = true;
      }
    }
  }
  return result;
};

const fetchBotIntegration = async () => {
  const integrations = store.getters["integration/integrations"];
  const botIntegration = filter(integrations, function (o) {
    return o.name == "bot";
  });
  if (size(botIntegration[0].integration) > 0) {
    const botId = clone(
      botIntegration[0].integration[0].integration_data.botId
    );
    let hashids = new Hashids("", 6);
    var hashedBotId = hashids.encode(botId);
    store.dispatch("bot/getBotList");
    if (!cookies.get("lenna_bot_id")) {
      store.dispatch("bot/setBotId", hashedBotId);
    } else {
      const activeBotId = hashids.decode(cookies.get("lenna_bot_id"));
      if (!validateActiveBot(integrations, activeBotId[0])) {
        store.dispatch("bot/setBotId", hashedBotId);
      }
    }
  } else {
    cookies.remove("lenna_bot_id");
  }
};

const throttle = debounce(() => {
  accessibilityChecker();
}, 500);

onBeforeMount(() => {
  throttle();
});

const isActiveModule = computed(() => {
  return (module) => {
    // Only treat a module as active when the first segment of the route name
    // matches its slug, e.g. 'crm' for 'crm.settings.*' routes.
    const currentRoot = route.name?.split(".")[0];
    return currentRoot === module?.slug;
  };
});

const isActiveSubmodule = computed(() => {
  return (module, submodule, submenu) => {
    // Active when route name matches module.submodule.submenu
    const routeParts = route.name?.split(".") || [];

    if (routeParts.length < 3) return false;
    if (routeParts[0] !== module?.slug) return false;
    if (routeParts[1] !== submodule?.slug) return false;
    if (routeParts[2] !== submenu?.slug) return false;

    return true;
  };
});

const onClickModule = (module) => {
  activeModule.value = module;
  hasSubModule.value =
    module.v3_menus.length > 0 && module?.type === "indirect";
  const haveBot = cookies.get("lenna_bot_id");
  if (module?.type === "direct" || (!haveBot && module.url === "studio")) {
    isExpanded.value = false;
    if (router.hasRoute(module.slug)) {
      return router.push({ name: module.slug }).catch((err) => {
        if (err.name !== "NavigationDuplicated") {
          console.warn("[V4Layout] navigation failed:", module.slug, err);
        }
      });
    }
    return;
  }

  if (module?.type === "indirect") {
    isExpanded.value = true;
  }
};

const ellipsis = (txt) => {
  const MAX_LENGTH = 16;
  if (txt.length > MAX_LENGTH) {
    return txt.substring(0, MAX_LENGTH) + "...";
  }
  return txt;
};

watchEffect(() => {
  try {
    const index = modulesSorted.value.findIndex(
      (m) => m.slug === route.name.split(".")[0]
    );
    activeModule.value = modulesSorted.value[index];
  } catch (err) {
    console.log(err);
  }
});

provide(
  "userData",
  computed(() => queryUserData.data)
);

watch(queryUserData.data, () => {
  if (!queryUserData.data?.value?.is_answer_survey) {
    emitter.emit("workspaceOnboardingModal:value", true);
  }
});
</script>

<template>
  <div class="vf-flex vf-min-h-screen vf-font-inter">
    <Navbar />
    <aside
      :class="
        cn(
          'main-sidebar vf-h-screen vf-overflow-y-auto vf-z-[99] vf-fixed vf-left-0 vf-top-0',
          'vf-bg-white vf-w-[72px] vf-border-r vf-border-neutral-200 vf-p-3'
        )
      "
    >
      <ul
        :class="
          cn(
            'vf-p-0 vf-m-0 vf-list-none',
            '[&>li]:vf-flex [&>li]:vf-items-center [&>li]:vf-justify-center',
            '[&>li]:vf-flex-col [&>li]:vf-py-1 [&>li]:vf-px-2 [&>li]:vf-text-center [&>li]:vf-text-[10px]',
            '[&>li:first-child]:vf-border-b [&>li:first-child]:vf-border-neutral-200',
            '[&>li:first-child]:vf-mt-0 [&>li:first-child]:vf-h-16 [&>li:first-child]:vf-mb-4'
          )
        "
      >
        <li>
          <router-link :to="{ name: 'home' }">
            <img
              class="vf-w-[30px]"
              src="/public/img/v4/lenna-blue.svg"
              alt="logo"
            />
          </router-link>
        </li>
        <template
          v-for="(module, i) in modulesSorted || []"
          :key="`modules_${i}`"
        >
          <li
            v-if="
              (modulesSorted || []).length >= 4 &&
              i === (modulesSorted || []).length - 3
            "
            class="vf-w-full vf-py-0 vf-border-none"
            aria-hidden="true"
          >
            <div
              class="vf-w-full vf-border-t vf-border-neutral-200 vf-my-2 vf-min-h-0"
            />
          </li>
          <li>
            <TooltipProvider :delay-duration="300">
              <Tooltip>
                <TooltipTrigger as-child>
                  <div
                    :class="[
                      'vf-p-2.5 vf-mb-0 vf-rounded-lg vf-cursor-pointer main-sidebar-item',
                      isActiveModule(module) && '__active',
                    ]"
                    @click="onClickModule(module)"
                  >
                    <Icon
                      :icon="module.id"
                      :isActive="isActiveModule(module)"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  :side-offset="8"
                  class="vf-z-[100]"
                >
                  {{ module.name }}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </li>
        </template>
      </ul>
    </aside>
    <div class="layout-content">
      <div
        v-if="activeModule?.type === 'indirect'"
        :class="[
          'collapsible-menu',
          isExpanded ? '__active' : '',
          hasSubModule ? '__has-sub-module' : '',
        ]"
      >
        <div
          class="mt-2"
          v-for="(submodule, i) in activeModule?.v3_menus || []"
          :key="`submodule_${i}`"
        >
          <router-link
            v-if="
              submodule.name === 'Manage Bot' &&
              router.hasRoute(`${activeModule.slug}.${submodule.slug}`)
            "
            :to="{ name: `${activeModule.slug}.${submodule.slug}` }"
          >
            <h6
              :class="[
                route.name.split('.')[1] === submodule.slug
                  ? 'vf-text-[#2A68EE]'
                  : 'vf-text-neutral-600',
                'vf-text-neutral-600 vf-font-semibold !vf-mb-3 hover:vf-text-neutral-500',
              ]"
            >
              {{ submodule.name }}
            </h6>
          </router-link>
          <h6
            v-else
            class="vf-text-neutral-900 vf-font-normal vf-text-sm !vf-mb-1.5 vf-translate-x-3"
          >
            {{ submodule.name }}
          </h6>
          <ul>
            <li
              v-for="(submenu, i) in submodule?.submenus || []"
              :key="`submenu_${i}`"
            >
              <router-link
                v-if="
                  router.hasRoute(
                    `${activeModule?.slug}.${submodule.slug}.${submenu.slug}`
                  )
                "
                :to="{
                  name: `${activeModule?.slug}.${submodule.slug}.${submenu.slug}`,
                }"
                :class="[
                  'expanded',
                  isActiveSubmodule(activeModule, submodule, submenu)
                    ? '__active'
                    : '',
                ]"
              >
                <Icon
                  :icon="`${submodule.id}-${submenu.id}`"
                  :isActive="
                    isActiveSubmodule(activeModule, submodule, submenu)
                  "
                  :isSubmenu="true"
                />
                <span :title="submenu.name">{{ ellipsis(submenu.name) }}</span>
              </router-link>
              <span
                v-else
                class="expanded vf-pointer-events-none vf-opacity-50 vf-text-neutral-400"
                :title="submenu.name"
              >
                <Icon
                  :icon="`${submodule.id}-${submenu.id}`"
                  :isSubmenu="true"
                />
                <span>{{ ellipsis(submenu.name) }}</span>
              </span>
            </li>
          </ul>
          <button
            class="toggler"
            @click="toggleExpand"
            style="width: auto !important"
          >
            <span class="vf-flex vf-gap-x-2">
              <IconLayoutSidebarRightExpand
                size="20"
                :color="'var(--primary-500)'"
                :class="[isExpanded && '__active']"
              />
              <span :class="['slide-transition', isExpanded && '__active']"
                >Hide Menu</span
              >
            </span>
          </button>
        </div>
      </div>
      <div
        :class="[
          'content-wrapper vf-bg-neutral-50',
          isExpanded ? '__active' : '',
          hasSubModule ? '__has-sub-module' : '',
        ]"
      >
        <div :class="cn('vf-py-4 vf-px-6', props.class)">
          <slot />
        </div>
      </div>
    </div>
  </div>

  <WorkspaceModal />
</template>

<style scoped lang="scss">
$icon-inactive: #9ba1b6;
$icon-active: var(--primary-500);
$border: #eeeeee;
$padding-left: 72px;
$padding-top: 72px;

.aside-left {
  height: 100vh;
  overflow-y: auto;
  z-index: 99;
  position: fixed;
  left: 0;
  top: 0;
  background-color: #fff;
  width: 100px;
  border-right: 1px solid $border;
  padding: 8px;
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 4px;
      text-align: center;
      font-size: 10px;
      &:first-child {
        border-bottom: 1px solid $border;
        margin-top: 0;
        height: 56px !important;
        margin-bottom: 16px;
      }
      img {
        width: 30px;
      }
      div {
        padding: 6px;
        margin-bottom: 4px;
        border-radius: 8px;
      }
      span {
        color: $icon-active; // $icon-inactive;
      }
    }
  }
}

.layout-content {
  width: 100%;
  height: 100%;
  flex: 1;
  background-color: #fff;
  padding-left: $padding-left;
  padding-top: $padding-top;
  display: flex;
  position: relative;
  // & > {
  //  min-height: 100%;
  //}
  .collapsible-menu {
    position: fixed;
    z-index: 98;
    left: $padding-left;
    top: $padding-top;
    background-color: #fff;
    border-right: 1px solid $border;
    min-height: 100%;
    padding: 8px;
    width: 80px;
    transition: 0.3s;
    /* When closed: fully hide off-screen (main sidebar 90px + menu 220px) */
    transform: translateY(-5px) translateX(-250px);
    overflow: hidden;
    &.__active {
      transform: translateY(0) translateX(0);
      width: 240px;
      h6 {
        margin-bottom: 16px;
        display: block;
        white-space: nowrap;
        font-size: 12px;
      }
      .expanded {
        border-left: 6px solid transparent;
        overflow: hidden;
        // display: flex;
        &.__active,
        &:hover {
          background-color: #f9f9f9;
          border-left: 3px solid $icon-active;
          color: $icon-active !important;
        }
        span {
          position: absolute;
          display: inline-flex;
          white-space: nowrap !important;
          margin-left: 4px;
          transform: translateX(0);
          transition: 0.3s;
          opacity: 1;
        }
      }
    }
    /* Only show submenu strip when expanded; when closed it stays hidden */
    &.__has-sub-module.__active {
      transform: translateY(0) translateX(0);
    }
    .expanded {
      position: relative;
      font-family: "Inter", sans-serif !important;
      letter-spacing: 0.25px;
      border: none;
      outline: none;
      padding: 12px 16px;
      width: 100%;
      border-radius: 6px;
      text-align: left;
      background-color: transparent;
      color: $icon-inactive;
      transition: 0.3s;
      overflow: hidden;
      &.__active,
      &:hover {
        background-color: #f9f9f9;
        // border-left: 6px solid $icon-active;
        border-left: none;
        color: $icon-active;
        svg {
          path {
            stroke: $icon-active !important;
          }
        }
      }
      // border-left: 6px solid transparent;

      span {
        position: absolute;
        transform: translateX(0px);
        opacity: 0;
        transition: 0.3s;
        white-space: nowrap;
      }
      svg {
        margin-right: 8px;
        display: inline-flex;
        margin: 0;
        transform: translateX(-2px);
      }
    }
    h6 {
      display: none;
    }
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      li {
        width: 100%;
        display: flex;
        margin-bottom: 2px;
        overflow: hidden;
        position: relative;
        font-size: 12px;
      }
    }
  }
  .toggler {
    position: absolute;
    bottom: 120px;
    outline: none;
    border: none;
    background-color: transparent;
    svg {
      transition: all 0.3s;
      transform: rotate(180deg);
      &.__active {
        transform: rotate(0deg);
      }
    }
    .slide-transition {
      white-space: nowrap;
      position: absolute;
      bottom: 0;
      transition: all 0.3s;
      transform: translateX(24px) translateY(-10px);
      opacity: 0;
      color: $icon-active;
      &.__active {
        transform: translateX(24px) translateY(0);
        opacity: 1;
      }
    }
  }
  .content-wrapper {
    overflow-x: hidden;
    width: 100%;
    padding-left: 0;
    // background-color: #fff;
    // min-height: 100vh;
    transition: all 0.3s;
    /* Only add padding when submenu is expanded; when closed it's hidden so no extra padding */
    &.__active {
      padding-left: 240px !important;
    }
    .content-padding {
      padding-left: 1000px !important;
    }
  }
}

.main-sidebar {
  .main-sidebar-item {
    transition: background-color 0.2s ease;
    &:hover {
      background-color: rgba(20, 52, 76, 0.08);
    }
    &.__active {
      background-color: var(--primary-600);
      &:hover {
        background-color: var(--primary-600);
      }
    }
  }
}
</style>
